import { Check, Monitor, Moon, Sun } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { THEMES, useTheme, type Theme } from '@/lib/theme'
import { useI18n, type MessageKey } from '@/i18n'

const ICONS: Record<Theme, typeof Sun> = { light: Sun, dark: Moon, system: Monitor }
const LABELS: Record<Theme, MessageKey> = {
  light: 'theme.light',
  dark: 'theme.dark',
  system: 'theme.system',
}

/**
 * Light / dark / system, as an explicit three-way choice.
 *
 * Not a two-state switch: a switch cannot express "follow my computer", so it
 * forces every visitor to pin a theme by hand and then stop matching their own
 * machine when evening comes.
 *
 * The trigger shows the theme that is ON SCREEN (`resolved`), so the icon never
 * disagrees with the page while the choice is 'system'.
 */
export function ThemeToggle() {
  const { theme, resolved, setTheme } = useTheme()
  const { t } = useI18n()
  const TriggerIcon = ICONS[resolved]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('theme.label')} title={t('theme.label')}>
          <TriggerIcon className="size-4" strokeWidth={1.75} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        <DropdownMenuLabel>{t('theme.label')}</DropdownMenuLabel>
        {THEMES.map((option) => {
          const Icon = ICONS[option]
          return (
            <DropdownMenuItem key={option} onSelect={() => setTheme(option)}>
              <Icon className="size-4" strokeWidth={1.75} />
              <span data-upbo-src="src/components/ThemeToggle.tsx:49" className="flex-1">{t(LABELS[option])}</span>
              {theme === option && <Check className="size-3.5" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
